import * as pathToRegexp from 'path-to-regexp'
import { FC } from 'react'
import {
  BookOpen,
  ExternalLink,
  FileText,
  Globe,
  Home,
  IconProps,
  PieChart,
  Share2,
  Tool,
} from 'react-feather'

import { BackOfficePrivilegeName } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { IconFinanceFile, IconGear, IconRating, IconUser } from '@avantstay/backoffice-vectors'
import { toQueryString } from '@avantstay/mobx-routing-utils'

import { IconBuilding, IconFinance, IconIot } from '@new/__ui/__vectors'

import { UserProfile } from '@contexts/Auth/AuthContext'
import { IS_PRODUCTION } from '@utils/constants'
import { Features } from '@utils/optimizely'

export const HELLO_ASHRIT_URL = 'https://hello-ashrit.prod.avantstay.com/'
export const LEARNING_HUB_URL = 'https://learning-hub.avantstay.com/learning'

interface RouteItem {
  exact?: boolean
  externalUrl?: string
  feature?: Features
  icon?: FC<IconProps>
  navHeader?: boolean
  path: string
  title: string
  Features?: Features
}

const internalRoutes = {
  // -------------------------
  // Section: General
  // -------------------------
  cancelScheduledSms: {
    exact: true,
    path: '/scheduledSms/cancel',
    title: 'Cancel Scheduled SMS',
  },
  error: {
    exact: true,
    path: '/error',
    title: 'Error',
  },
  help: {
    exact: true,
    path: '/help/:tab?',
    title: 'Help',
  },
  home: { path: '/', title: 'Home' },
  login: { path: '/login', title: 'Login' },
  oauthConsent: {
    exact: true,
    path: '/oauth-consent',
    title: 'OAuth Consent',
  },
  // -------------------------
  // Section: Dashboard
  // -------------------------
  dashboard: {
    icon: PieChart,
    navHeader: true,
    path: '/dashboard',
    title: 'Dashboard',
  },
  // -------------------------
  // Section: Markets
  // -------------------------
  markets: {
    feature: Features.Regions,
    icon: Globe,
    navHeader: true,
    path: '/markets',
    title: 'Markets',
  },
  marketsDailyAgenda: {
    path: '/markets/daily-agenda',
    title: 'Daily Agenda',
  },
  marketsIotOperations: {
    path: '/markets/iot-operations',
    title: 'IoT Operations',
  },
  marketsTaskManagement: {
    path: '/markets/task-management',
    title: 'Task Management',
  },
  // -------------------------
  // Section: Property
  // -------------------------
  properties: {
    exact: true,
    icon: Home,
    navHeader: true,
    path: '/properties/:viewMode?',
    title: 'Properties',
  },
  propertiesDistributionConnections: {
    exact: true,
    path: '/properties/:id/distribution/connections',
    title: 'Distributions Connections',
  },
  propertiesDistributionListingStatus: {
    exact: true,
    path: '/properties/:id/distribution/listing-status',
    title: 'Distribution Listing Status',
  },
  property: { path: '/properties/:id', title: 'Property' },
  propertyAmenities: {
    exact: true,
    feature: Features.PropertyAmenities,
    path: '/properties/:id/amenities/:section?',
    title: 'Amenities',
  },
  propertyDashboard: {
    exact: true,
    path: '/properties/:id/dashboard',
    title: 'Property Dashboard',
  },
  propertyDistributionChannels: {
    exact: true,
    feature: Features.PropertyAmenities,
    path: '/properties/:id/distribution/:section?/:viewMode?',
    title: 'Listings & Distribution',
  },
  propertyEntryInformation: {
    exact: true,
    path: '/properties/:id/entry-information',
    title: 'Property Entry Information',
  },
  propertyFinances: {
    path: '/properties/:id/finances',
    title: 'Property Finances',
    exact: true,
  },
  propertyOwnerStatements: {
    path: '/properties/:id/owner-statements',
    title: 'Owner Statements',
    exact: true,
  },
  propertyHomeownerInfo: {
    exact: true,
    path: '/properties/:id/homeowner-info/:section?/:viewMode?',
    title: 'Owner Documents',
  },
  propertyCommunications: {
    exact: true,
    path: '/properties/:id/communications/:section?',
    title: 'Communications',
  },
  propertyInfo: {
    exact: true,
    path: '/properties/:id/info/:section?/:viewMode?',
    title: 'Property Info',
  },
  propertyInHouseMonitoring: {
    exact: true,
    path: '/properties/:id/in-house-monitoring',
    title: 'In-House Monitoring',
  },
  propertyInternalConfiguration: {
    exact: true,
    path: '/properties/:id/internal-configuration/:section?/:viewMode?',
    title: 'Internal Configuration',
  },
  propertyLayout: {
    exact: true,
    path: '/properties/:id/layout/:section?/:viewMode?',
    title: 'Property Layout',
  },
  propertyManual: {
    exact: true,
    path: '/properties/:id/manual/:section?',
    title: 'Property Manuals',
  },
  propertyOnboarding: {
    exact: true,
    path: '/properties/:id/onboarding/:section?/:viewMode?',
    title: 'Onboarding',
  },
  propertyPhotoGallery: {
    exact: true,
    path: '/properties/:id/photo-gallery',
    title: 'Property Photo Gallery',
  },
  propertyRevenueConfiguration: {
    exact: true,
    feature: Features.PropertyRevenueConfiguration,
    path: '/properties/:id/revenue-configuration/:section?/:viewMode?',
    title: 'Revenue Configuration',
  },
  propertyHistory: {
    exact: true,
    path: '/properties/:id/history',
    title: 'History',
  },
  propertyReviews: {
    exact: true,
    path: '/properties/:id/reviews',
    title: 'Reviews',
  },
  propertyRooms: {
    exact: true,
    path: '/properties/:id/rooms',
    title: 'Property Room Details',
  },
  propertyServicesAndVendors: {
    exact: true,
    path: '/properties/:id/services-and-vendors',
    title: 'Services & Vendors',
  },
  propertyStats: {
    path: '/properties/:id/stats',
    exact: true,
    title: 'Property Stats',
  },
  propertyTaxesAndFees: {
    exact: true,
    feature: Features.TaxesAndFees,
    path: '/properties/:id/taxes-and-fees',
    title: 'Taxes and Fees',
  },
  propertyThingsToDo: {
    exact: true,
    path: '/properties/:id/things-to-do/:section?',
    title: 'Things To Do',
  },
  propertyValueAddedServices: {
    exact: true,
    path: '/properties/:id/value-added-services',
    title: 'Property Value-Added Services',
  },
  propertyVendorRate: {
    path: '/properties/vendor_rates',
    exact: true,
    title: 'Vendor Rates',
  },
  propertyBulkEdit: {
    path: '/properties/bulk-edit',
    exact: true,
    title: 'Property Bulk Edit',
  },
  propertyWorkOrderTemplates: {
    exact: true,
    path: '/properties/:id/work-order-templates/:template?/:section?/:item?',
    title: 'Property Work Order Templates',
  },
  propertyWorkOrderTemplatesHistory: {
    exact: true,
    path: '/properties/:id/work-order-templates/:template/history',
    title: 'Property Work Order Templates - Changes History',
  },
  propertyPolicyAndPermits: {
    exact: true,
    path: '/properties/:id/policy-and-permits/:section?',
    title: 'Policy & Permits',
  },
  // -------------------------
  // Section: Listing
  // -------------------------
  allListingDrafts: {
    exact: true,
    path: '/listing-draft',
    title: 'All Listing Drafts',
  },
  listingDraft: {
    exact: true,
    path: '/listing-draft/:id',
    title: 'Listing Draft',
  },
  // -------------------------
  // Section: Buildings
  // -------------------------
  buildings: {
    path: '/buildings',
    title: 'Buildings',
    icon: IconBuilding,
    exact: true,
    navHeader: true,
  },
  performanceHub: {
    path: '/buildings/performance-hub',
    title: 'Performance Hub',
    exact: true,
  },
  buildingsList: {
    path: '/buildings/list',
    title: 'Buildings List',
    exact: true,
  },
  // -------------------------
  // Section: Hotel
  // -------------------------
  hotel: { path: '/hotels/:id', title: 'Hotel' },
  hotelAmenities: {
    exact: true,
    path: '/hotels/:id/amenities/:section?/:viewMode?',
    title: 'Hotel Amenities',
  },
  hotelDistribution: {
    exact: true,
    path: '/hotels/:id/distribution/:section?/:viewMode?',
    title: 'Hotel Listings & Distribution',
  },
  hotelInfo: {
    exact: true,
    path: '/hotels/:id/info/:section?/:viewMode?',
    title: 'Hotel Info',
  },
  hotelLayout: {
    exact: true,
    path: '/hotels/:id/layout/:section?/:viewMode?',
    title: 'Hotel Layout',
  },
  hotelPhotoGallery: {
    exact: true,
    path: '/hotels/:id/photo-gallery',
    title: 'Hotel Photo Gallery',
  },
  hotelRules: {
    exact: true,
    path: '/hotels/:id/rules',
    title: 'Hotel Rules',
  },
  hotelThingsToDo: {
    exact: true,
    path: '/hotels/:id/things-to-do',
    title: 'Hotel Things To Do',
  },
  // -------------------------
  // Section: Booking
  // -------------------------
  booking: {
    path: '/bookings/:hash',
    title: 'Booking',
  },
  bookingAccountingTimeline: {
    exact: true,
    path: '/bookings/:hash/accounting-timeline/:section?',
    title: 'Booking Accounting Timeline',
  },
  bookingAgreements: {
    exact: true,
    path: '/bookings/:hash/agreements',
    title: 'Agreements',
  },
  bookingCases: {
    exact: true,
    path: '/bookings/:hash/cases',
    title: 'Cases',
  },
  bookingConflicts: {
    exact: true,
    path: '/bookings/booking-conflicts',
    title: 'Booking Conflicts',
  },
  bookingGuestDamage: {
    exact: true,
    path: '/bookings/:hash/guest-damage/:section?/:viewMode?',
    title: 'Guest Damage',
  },
  bookingGuests: {
    exact: true,
    path: '/bookings/:hash/guests',
    title: 'Guests',
  },
  bookingHistory: {
    exact: true,
    path: '/bookings/:hash/history',
    title: 'History',
  },
  bookingInfo: {
    exact: true,
    path: '/bookings/:hash/info/:section?/:viewMode?',
    title: 'Booking Info',
  },
  bookingMessages: {
    exact: true,
    path: '/bookings/:hash/messages/:selectedMessageId?',
    title: 'Messages',
  },
  bookingPaymentAndServices: {
    exact: true,
    path: '/bookings/:hash/payment-and-services',
    title: 'Booking Payment And Services',
  },
  bookingPaymentDetails: {
    exact: true,
    path: '/bookings/:hash/payment-details/:section?/:viewMode?',
    title: 'Booking Payment Details',
  },
  bookingRefundRequests: {
    exact: true,
    path: '/bookings/requests/refunds',
    title: 'Refunds Requests',
  },
  bookings: {
    exact: true,
    path: '/bookings/all',
    title: 'Bookings',
  },
  bookingReviews: {
    exact: true,
    path: '/bookings/:hash/reviews',
    title: 'Reviews',
  },
  bookingServicesRequests: {
    exact: true,
    feature: Features.ValueAddedServicesRequests,
    path: '/bookings/requests/services',
    title: 'Service Requests',
  },
  bookingSMSConversations: {
    exact: true,
    path: '/bookings/:hash/in-stay-conversation',
    title: 'In-stay Conversation',
  },
  bookingTimeline: {
    exact: true,
    path: '/bookings/:hash/timeline',
    title: 'Timeline',
  },
  bookingUserSessions: {
    exact: true,
    path: '/bookings/:hash/user-sessions',
    title: 'Guest Damage',
  },
  bookingValueAddedServices: {
    exact: true,
    path: '/bookings/:hash/value-added-services',
    title: 'Booking Value Added Services',
  },
  fastTapeChart: {
    icon: FileText,
    exact: true,
    navHeader: true,
    path: '/bookings/tapechart',
    title: 'Bookings',
  },
  groupsEvents: {
    exact: false,
    path: '/groups-events',
    title: 'Groups',
  },
  createGroupEvent: {
    exact: true,
    path: '/groups-events/new',
    title: 'Create Event',
  },
  groupEventSection: {
    exact: true,
    path: '/groups-events/details/:id/:section',
    title: 'Group & Event',
  },
  groupEvent: {
    exact: true,
    path: '/groups-events/details/:id',
    title: 'Group & Event',
  },
  hotelTapeChart: {
    icon: FileText,
    exact: true,
    path: '/bookings/hotels-tapechart',
    title: 'Bookings',
  },
  idVerification: {
    exact: true,
    path: '/bookings/id-verification',
    title: 'ID Verification',
  },
  // -------------------------
  // Section: Operations
  // -------------------------
  operations: {
    icon: Tool,
    feature: Features.Operations,
    navHeader: true,
    path: '/operations/:feature?/:id?',
    title: 'Operations',
  },
  operationOpsReviewPulse: {
    exact: true,
    path: '/operations/ops-review/pulse',
    title: 'Ops Review Pulse',
  },
  operationOpsReviewMbr: {
    exact: true,
    path: '/operations/ops-review/mbr',
    title: 'Ops Review MBR',
  },
  operationBookingBasedCase: {
    exact: true,
    path: '/operations/autocase-generator',
    title: 'AutoCase Generator',
  },
  operationCasesTapeChart: {
    exact: true,
    path: '/operations/cases-tape-chart',
    title: 'Cases Tape Chart',
  },
  operationExpenses: {
    exact: true,
    path: '/operations/expenses',
    title: 'Expenses',
  },
  netsuitePayables: {
    exact: true,
    path: '/finance/netsuite-payables',
    title: 'NetSuite Payables',
  },
  operationGuestDamagesReport: {
    exact: true,
    path: '/operations/guest-damages-report',
    title: 'Operation Guest Damages Report',
  },
  operationTaxonomy: {
    exact: true,
    path: '/operations/taxonomy',
    title: 'Case Taxonomy',
  },
  operationIssues: {
    exact: true,
    path: '/operations/work-order/:id/issues',
    title: 'Operation Issues',
  },
  operationMarkets: {
    exact: true,
    path: '/operations/markets/:id?',
    title: 'Operation Markets',
  },
  operationCases: {
    exact: true,
    path: '/operations/cases',
    title: 'Operation Cases',
  },
  operationGuestInbox: {
    exact: true,
    path: '/operations/guest-inbox',
    title: 'Guest Inbox',
  },
  operationRecurringCases: {
    exact: true,
    path: '/operations/recurring-cases',
    title: 'Operation Recurring Cases',
  },
  operationRecurringExpenses: {
    exact: true,
    path: '/operations/routine-and-mt-expenses',
    title: 'Operation Recurring Expenses',
  },
  operationReview: {
    exact: true,
    path: '/operations/work-order/:id/review',
    title: 'Operation Review',
  },
  operationSkipWalkthrough: {
    exact: true,
    path: '/operations/work-order/:id/skip-walkthrough',
    title: 'Operation Skip & Walkthrough',
  },
  operationVendors: {
    exact: true,
    path: '/operations/vendors',
    title: 'Operation Vendors',
  },
  operationVendorEmailInvoices: {
    exact: true,
    path: '/operations/vendor-email-invoices',
    title: 'Vendor Email Invoices',
  },
  operationVendorEmailInvoice: {
    exact: true,
    path: '/operations/vendor-email-invoices/:id',
    title: 'Vendor Email Invoice',
  },
  operationWorkOrder: {
    exact: true,
    path: '/operations/work-order',
    title: 'Operation Details',
  },
  sfdcWorkOrder: {
    exact: true,
    path: '/operations',
    title: 'Operation Details',
  },
  // -------------------------
  // Section: People
  // -------------------------
  people: {
    feature: Features.People,
    icon: IconUser,
    navHeader: true,
    path: '/people',
    title: 'People',
  },
  bookingLeads: {
    path: '/people/booking-leads/:id?',
    title: 'Booking Leads',
  },
  guests: {
    feature: Features.Guests,
    path: '/people/guests/:id?',
    title: 'Guests',
  },
  homeowners: {
    path: '/people/homeowners/:id?',
    title: 'Homeowners',
  },
  lighthouseAccess: {
    path: '/people/lighthouse/:id?',
    title: 'Lighthouse Access',
  },
  marketAssignments: {
    path: '/people/market-assignments',
    title: 'Market Assignments',
    navHeader: false,
    exact: false,
  },
  // -------------------------
  // Section: Revenue
  // -------------------------
  accountingResolutionCenter: {
    path: '/revenue/accounting-resolution-center',
    title: 'Accounting Resolution Center',
  },
  floorRateCalculator: {
    exact: true,
    feature: Features.PricingTool,
    icon: IconFinance,
    path: '/revenue/floor-rate-calculator',
    title: 'Floor Rate Calculator',
  },
  fees: {
    exact: true,
    feature: Features.TaxesAndFees,
    path: '/revenue/fees',
    title: 'Fees',
  },
  defaultMarkups: {
    exact: true,
    feature: Features.Markups,
    path: '/revenue/settings/default-markups',
    title: 'Default Markups',
  },
  markups: {
    exact: true,
    feature: Features.Markups,
    path: '/revenue/settings/markups',
    title: 'Markups',
  },
  ownerIncomeDashboard: {
    exact: true,
    feature: Features.OwnerIncomeDashboard,
    path: '/revenue/revenue-report',
    title: 'Revenue Report',
  },
  pricingTapeChart: {
    exact: true,
    feature: Features.TaxesAndFees,
    path: '/revenue/pricing-tape-chart',
    title: 'Pricing Tape Chart',
  },
  pricingTool: {
    exact: true,
    feature: Features.PricingTool,
    icon: IconFinance,
    path: '/revenue/pricing-tool',
    title: 'Pricing Tool',
  },
  pricingToolMarkets: {
    exact: true,
    feature: Features.PricingTool,
    path: '/revenue/pricing-tool-markets',
    title: 'Pricing Tool Markets',
  },
  revenueAnalystHome: {
    exact: true,
    // TODO: route URL still references 'analyst-home' for
    // backwards compatibility. Consider renaming to
    // 'revenue-home' in a follow-up with proper redirect
    // from the old URL.
    path: '/revenue/analyst-home',
    title: 'Revenue Home',
  },
  revenueTickets: {
    exact: true,
    path: '/revenue/tickets',
    title: 'Tickets',
  },
  revenueAgents: {
    path: '/revenue/agents',
    title: 'Revenue Agents',
  },
  revenueAgentsProperties: {
    path: '/revenue/agents/properties/:propertyId',
    title: 'Revenue Agents - Property',
  },
  revenueAgentsActions: {
    path: '/revenue/agents/actions',
    title: 'Revenue Agents - Actions',
  },
  revenueAgentsLearning: {
    path: '/revenue/agents/learning',
    title: 'Revenue Agents - Learning',
  },
  revenueAgentsWorkflows: {
    path: '/revenue/agents/workflows',
    title: 'Revenue Agents - Workflows',
  },
  revenueAgentsChanges: {
    path: '/revenue/agents/changes',
    title: 'Revenue Agents - Changes',
  },
  revenueAgentsFeedbackReview: {
    path: '/revenue/agents/feedback-review',
    title: 'Revenue Agents - Feedback Review',
  },
  revenueAgentsAutoExecution: {
    path: '/revenue/agents/auto-execution',
    title: 'Revenue Agents - Queue',
  },
  revenue: {
    exact: true,
    feature: Features.TaxesAndFees,
    icon: IconFinance,
    navHeader: true,
    path: '/revenue',
    title: 'Revenue',
  },
  revenueBookingsTapeChart: {
    exact: true,
    feature: Features.TaxesAndFees,
    path: '/revenue/revenue-tape-chart',
    title: 'Revenue Tape Chart',
  },
  revenueMapping: {
    exact: true,
    feature: Features.TaxesAndFees,
    icon: IconFinance,
    path: '/revenue/revenue-mapping',
    title: 'Revenue Mapping',
  },
  revenueSettings: {
    feature: Features.RevenueSettings,
    path: '/revenue/settings',
    title: 'Revenue Settings',
  },
  seasonGroups: {
    exact: true,
    feature: Features.SeasonGroups,
    path: '/revenue/settings/seasons-groups',
    title: 'Settings',
  },
  seasonalCancellationPolicies: {
    exact: true,
    path: '/revenue/settings/seasonal-cancellation-policies',
    title: 'Seasonal Cancellation Policies',
  },
  seasons: {
    exact: true,
    feature: Features.Seasons,
    path: '/revenue/settings/seasons',
    title: 'Seasons',
  },
  taxes: {
    exact: true,
    feature: Features.TaxesAndFees,
    icon: IconFinance,
    path: '/revenue/taxes',
    title: 'Taxes',
  },
  underwriting: {
    path: '/revenue/underwriting/:underwritingId?',
    title: 'Underwriting',
    exact: true,
  },
  yieldGroups: {
    exact: true,
    feature: Features.YieldGroups,
    path: '/revenue/settings/yield-groups',
    title: 'Yield Groups',
  },
  yieldMaps: {
    exact: true,
    feature: Features.YieldMaps,
    path: '/revenue/settings/yield-maps',
    title: 'Yield Maps',
  },
  // -------------------------
  // Section: Ratings
  // -------------------------
  ratings: {
    icon: IconRating,
    navHeader: true,
    path: '/ratings',
    title: 'Ratings',
  },
  ratingsAirbnbQuality: {
    exact: true,
    path: '/ratings/airbnb-quality',
    title: 'Airbnb Quality',
  },
  ratingsMarketsReview: {
    exact: true,
    path: '/ratings/reviews/:id',
    title: 'Review Markets',
  },
  ratingsNpsByChannel: {
    exact: true,
    path: '/ratings/nps-by-channel',
    title: 'NPS by Channel',
  },
  ratingsPerformance: {
    exact: true,
    path: '/ratings/performance',
    title: 'Ratings',
  },
  ratingsSuperhost: {
    exact: true,
    path: '/ratings/superhost',
    title: 'Superhost',
  },
  ratingsReviewsManagement: {
    exact: true,
    path: '/ratings/review-management',
    title: 'Ratings',
  },
  // -------------------------
  // Section: Marketing
  // -------------------------
  couponBlackoutDates: {
    exact: true,
    path: '/marketing/coupon-blackout-dates',
    title: 'Coupon Blackout Dates',
  },
  coupons: {
    exact: true,
    feature: Features.Coupons,
    path: '/marketing/coupons',
    title: 'Coupons',
  },
  marketing: {
    icon: Share2,
    navHeader: true,
    path: '/marketing',
    title: 'Marketing',
  },
  quotes: {
    exact: true,
    feature: Features.Quotes,
    path: '/marketing/quotes',
    title: 'Quotes',
  },
  quotesHistory: {
    exact: true,
    feature: Features.Quotes,
    path: '/marketing/quotes/history',
    title: 'Quotes History',
  },
  vrmCoupons: {
    exact: true,
    path: '/marketing/vrm-coupons',
    title: 'VRM Coupons',
  },
  // -------------------------
  // Section: Finance
  // -------------------------
  finance: {
    icon: IconFinanceFile,
    navHeader: true,
    path: '/finance',
    title: 'Finance',
  },
  unitEconomics: {
    exact: true,
    path: '/finance/unit-economics',
    title: 'Unit Economics',
  },
  discountTapeChart: {
    path: '/revenue/seasonal-discounts',
    navHeader: false,
    title: 'Seasonal Discounts',
  },
  // -------------------------
  // Section: External tools
  // -------------------------
  learningHub: {
    externalUrl: LEARNING_HUB_URL,
    icon: BookOpen,
    navHeader: true,
    // Menu consumers open `externalUrl` instead of routing to `path`
    path: LEARNING_HUB_URL,
    title: 'Learning',
  },
  helloAshrit: {
    externalUrl: HELLO_ASHRIT_URL,
    icon: ExternalLink,
    navHeader: true,
    // Menu consumers open `externalUrl` instead of routing to `path`
    path: HELLO_ASHRIT_URL,
    title: 'Hello Ashrit',
  },
  // -------------------------
  // Section: Settings
  // -------------------------
  aiSummaries: {
    exact: true,
    path: '/settings/ai-summaries',
    title: 'AI Summaries',
  },
  agentDocumentBuilder: {
    exact: true,
    path: '/settings/document-builder',
    title: 'Document Builder',
  },
  customServiceTaxonomy: {
    exact: true,
    path: '/settings/custom-service-taxonomy',
    title: 'Customer Support Taxonomy',
  },
  blacklist: {
    exact: true,
    path: '/settings/blacklist',
    title: 'Blacklist',
  },
  customFields: {
    exact: true,
    path: '/settings/custom-fields',
    title: 'Custom Fields',
  },
  globalWorkOrderTemplates: {
    exact: true,
    path: '/settings/work-order-templates/:template?/:section?/:item?',
    title: 'Work Order Templates',
  },
  globalWorkOrderTemplatesHistory: {
    exact: true,
    path: '/settings/work-order-templates/history/:template?',
    title: 'Work Order Templates - Changes History',
  },
  globalWorkOrderTemplatesPermissions: {
    exact: true,
    path: '/settings/work-order-templates/permissions',
    title: 'Work Order Templates - Permissions',
  },
  iotDevices: {
    exact: true,
    icon: IconIot,
    path: '/settings/iot-devices',
    title: 'Iot Devices',
  },
  listingIssueSolutions: {
    exact: true,
    path: '/settings/listing-issue-solutions',
    title: 'Listing Issue Solutions',
  },
  otaAmenitiesMapping: {
    exact: true,
    path: '/settings/ota-amenities-mapping',
    title: 'OTA Amenities Mapping',
  },
  otaTaxesAndFeesMapping: {
    exact: true,
    path: '/settings/ota-taxes-fees-mapping',
    title: 'OTA Taxes and Fees Mapping',
  },
  settings: {
    icon: IconGear,
    navHeader: true,
    path: '/settings',
    title: 'Settings',
  },
  smsGeoPermissions: {
    exact: true,
    path: '/settings/sms-geo-permissions',
    title: 'SMS Geo Permissions',
  },
  sharedManuals: {
    exact: true,
    path: '/settings/property-manuals',
    title: 'Property Manuals',
  },
  sharedThingsToDo: {
    exact: true,
    path: '/settings/things-to-do',
    title: 'Things To Do',
  },
  systemBehavior: {
    exact: true,
    path: '/settings/system-behavior',
    title: 'System Behavior',
  },
  termsAndConditions: {
    path: '/settings/terms-conditions',
    title: 'Terms & Conditions',
    exact: true,
  },
  termsAndConditionsDiff: {
    path: '/settings/terms-conditions/diff/:version1?/:version2?',
    title: 'Terms & Conditions Difference',
    exact: true,
  },
  termsAndConditionsPreview: {
    path: '/settings/terms-conditions/preview/:versionId?',
    title: 'Terms & Conditions Preview',
    exact: true,
  },
  usersAndPermissions: {
    exact: true,
    feature: Features.UsersAndPermissionsSettings,
    path: '/settings/users-and-permissions/:section?',
    title: 'Users & Permissions',
  },
  // ---- TO DELETE
  tapeChart: {
    path: '/bookings/tapechart-legacy',
    title: 'Tape Chart (Legacy)',
    exact: true,
  },
  // ----
}

export type RouteKey = keyof typeof internalRoutes
export const ROUTES: Record<RouteKey, RouteItem> = internalRoutes

export const mountRouteUrl = (
  path: string,
  params?: Record<string, unknown>,
  queries?: Record<string, string | undefined>,
) => {
  const toPath = pathToRegexp.compile(path)

  if (queries) {
    const definedQueries = Object.fromEntries(
      Object.entries(queries).filter(([, value]) => value != null && value !== ''),
    ) as Record<string, string>
    const parsedQueries = toQueryString(definedQueries)

    return parsedQueries ? `${toPath(params)}?${parsedQueries}` : toPath(params)
  }

  return toPath(params)
}

export function openLink(link: string, target = '_blank') {
  window.open(link, target, 'noopener')
}

export const getMainMenuItems = (user?: UserProfile) =>
  (Object.keys(ROUTES) as (keyof typeof ROUTES)[])
    .filter(it => ROUTES[it].navHeader)
    .filter(it => {
      switch (it) {
        case 'finance':
          return user?.permissions?.includes(BackOfficePrivilegeName.financeIncomeStatementRead)
        case 'helloAshrit':
          return user?.permissions?.includes(BackOfficePrivilegeName.admin)
        default:
          return true
      }
    }) as RouteKey[]

export const createLink = (linkTitle: string, params: string) => ({
  title: linkTitle,
  url: `http${IS_PRODUCTION ? 's' : ''}://${window.location.host}${params}`,
})
